// Singleton background-music controller. Owns the AudioContext and the two
// tracks: the round track, faded in when a round is live, and the menu track,
// which plays whenever no game holds the sound (not between rounds). Plus
// autoplay-after-first-gesture (browsers block audio until the user interacts)
// and live volume/mute from settings. App.tsx toggles playback from its view
// state; GameComponent feeds an intensity signal for the adaptive track.

import type { MusicSource } from "./music-sources";
import { createFileTrack } from "./music-tracks";
// The music (public/music/, picked 2026-10-22): DavidKBD's Pink Bloom Pack, CC BY
// 4.0, credited on the About page. 05 "Western Cyberhorse" in rounds, 08 "Lost
// Spaceship's Signal" in the menus.
const ROUND_TRACK = "round-western-cyberhorse";
const MENU_TRACK = "menu-lost-spaceships-signal";
import { playCountdownCount, playCountdownGo } from "./countdown";
import { playSwitcharoonie, playPortalOpen } from "./sfx";
import { SOUNDS, type SoundId } from "./sounds";
import { getSettings, subscribeSettings, Settings } from "../shared/settings";

// Music swells in over the ~3s start countdown so it blooms to full at "GO",
// then fades out gently when the round ends. The fade-in is an ease-in curve
// (power > 1): it stays quiet through "3" and "2", then blooms up into "GO".
const FADE_IN_SEC = 2.8;
const FADE_OUT_SEC = 2.8;
const FADE_IN_POWER = 3;
const FADE_IN_CURVE = (() => {
  const N = 64;
  const curve = new Float32Array(N);
  for (let i = 0; i < N; i++) curve[i] = Math.pow(i / (N - 1), FADE_IN_POWER);
  return curve;
})();
// Between rounds the round track keeps playing, ducked to half: down quickly as
// the results come up, back up as the next round's countdown shows its "3".
const DUCK_LEVEL = 0.5;
const DUCK_SEC = 0.8;
const UNDUCK_SEC = 1.5;
// Countdown beeps play at a fixed level, independent of the music-volume slider
// (chosen so they sit roughly where they did at the default 70% volume).
const SFX_LEVEL = 0.4;
// Music is background: cap its share of the master so SFX/voice can sit on top
// later, and so 100% volume isn't fatiguing.
const MUSIC_HEADROOM = 0.55;
// The menu track eases in and out more gently than the round track.
const MENU_FADE_IN_SEC = 3;
const MENU_FADE_OUT_SEC = 1.2;

// Live audio state the game feeds the controller each frame.
export interface GameAudioState {
  intensity: number; // 0..1 musical intensity
  countdownLeft: number; // seconds until the round starts; <= 0 once playing
  countdownLength?: number; // the countdown proper (after the roll call); beeps only within it
  crashes: number; // cumulative "worm_died" events this round, one per worm wipeout (crash SFX)
  ownCrashes?: number; // ... of which our own worms (the heavier crash instead of the plain one)
  pickups: number; // cumulative power-up pickups this round (pickup SFX)
  // ... by colour: green helps the grabber, red hits the rivals (and fires missiles),
  // blue hits everyone. Without them every pickup sounds blue.
  pickupsGreen?: number;
  pickupsRed?: number;
  spawns?: number; // cumulative power-ups appearing this round
  missileHits?: number; // cumulative missiles landing this round
  switches: number; // cumulative switcharoonie pickups this round (swap SFX)
  // Switcharoonie portal marker: the game-time the current switch's portals open once
  // reached, 0 otherwise. Each new nonzero value fires the portal riser once.
  portalOpenAt?: number;
  // When set (a paused/scrubbing replay), keep the music going but fire no SFX —
  // the counters below are still synced so resuming doesn't retro-fire them.
  suppressSfx?: boolean;
  // No 3-2-1 / GO beeps for this game (the ranked search's practice round);
  // every other SFX still plays.
  silentCountdown?: boolean;
}

class MusicController {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null; // mute gate (0/1) -> destination
  private musicGain: GainNode | null = null; // music-volume slider scales this only
  private sfxGain: GainNode | null = null; // fixed level for countdown beeps
  private track: { src: MusicSource; gain: GainNode; playing: boolean } | null = null;
  private menuTrack: { src: MusicSource; gain: GainNode; playing: boolean } | null = null;
  private files = new Map<string, AudioBuffer | null>(); // recorded SFX (sounds.ts), decoded on unlock

  private wantPlaying = false;
  private ducked = false;
  private duckArmed = false; // the next round is on: lift the duck at its countdown
  private trackOn = false; // the round track was last faded in (not out)
  private unlocked = false;
  private initialised = false;

  private gameAudioProvider: (() => GameAudioState | null) | null = null;
  // A game that sounds over whatever the provider above is (the ranked
  // search's practice round, over the match waiting underneath it), and a hold
  // that keeps the music off meanwhile: its SFX, no track.
  private overrideProvider: (() => GameAudioState | null) | null = null;
  private musicHeld = false;
  private intensity = 0.3;
  private rafId = 0;
  private unlockHandler: (() => void) | null = null;

  // Countdown-beep bookkeeping: which integer ("3"/"2"/"1") last beeped, and
  // whether the final "GO" blip has fired for the current round.
  private lastBeepCount = -1;
  private goFired = false;
  // Cumulative crash/pickup counts already sounded, so we only fire on new ones.
  private lastCrashes = 0;
  private lastPickups = 0;
  private lastGreen = 0;
  private lastRed = 0;
  private lastSpawns = 0;
  private lastMissileHits = 0;
  private lastOwnCrashes = 0;
  private lastSwitches = 0;
  // Switcharoonie portal marker already sounded (see GameAudioState).
  private lastPortalOpenAt = 0;
  private lastCrashTime = 0; // throttle so a mass pile-up doesn't machine-gun

  // Call once at app startup. Arms gesture-unlock listeners and subscribes to
  // settings; does NOT create an AudioContext yet (that waits for a user gesture).
  init(): void {
    if (this.initialised || typeof window === "undefined") return;
    this.initialised = true;

    subscribeSettings((s) => this.applyVolume(s));

    // Only the tab (or the Android app) you are looking at makes sound: hidden,
    // the audio pauses (music and effects stop, their timing frozen); shown, it
    // picks up where it was.
    document.addEventListener("visibilitychange", () => this.applyVisibility());

    this.unlockHandler = () => this.unlock();
    ["pointerdown", "keydown", "touchstart"].forEach((evt) =>
      window.addEventListener(evt, this.unlockHandler as EventListener, { passive: true }),
    );

    // Try now, without waiting for a gesture: browsers that allow sound on load
    // (Chrome for an installed app, the Android app included, or a site you often
    // play media on) start the menu music as the page opens. Elsewhere the context
    // stays suspended and the first gesture above starts it.
    this.ensureContext();
    const ctx = this.ctx as AudioContext | null;
    if (ctx) {
      if (ctx.state === "running" && document.hidden) ctx.suspend().catch(() => {});
      if (ctx.state === "running" && !document.hidden) this.unlock();
      else {
        ctx.addEventListener("statechange", () => { if (ctx.state === "running") this.unlock(); });
        if (!document.hidden) ctx.resume().catch(() => {}); // (a background tab: on showing it, applyVisibility)
      }
    }
  }

  // Start/stop the music. Fades if audio is live; otherwise remembers the wish
  // and applies it on the first user gesture.
  setPlaying(playing: boolean): void {
    const was = this.wantPlaying;
    this.wantPlaying = playing;
    if (was !== playing && this.playingListener) this.playingListener(playing);
    if (this.unlocked) { this.applyTrack(); this.applyMenuTrack(); }
  }

  // Told when a game's music starts and stops (a match's rounds, the match
  // over): a game site's gameplay events (shared/portal.ts).
  private playingListener: ((playing: boolean) => void) | null = null;
  onPlayingChange(fn: (playing: boolean) => void): void {
    this.playingListener = fn;
  }

  // A game site's own mute (CrazyGames' muteAudio): it wins over the player's.
  private externalMute = false;
  setExternalMute(on: boolean): void {
    this.externalMute = on;
    this.applyVolume(getSettings());
  }

  // GameComponent registers a getter for the live game's audio state (or null
  // when no game is live). The controller smooths intensity into the track and
  // fires countdown / crash / pickup SFX off the same signal.
  setGameAudioProvider(fn: (() => GameAudioState | null) | null): void {
    this.gameAudioProvider = fn;
    if (this.unlocked) this.applyMenuTrack();
  }

  // Sound this game's SFX instead of the provider's until cleared (null).
  // Let go of the provider only if it is still `fn`: two games can be mounted at
  // once (playing Play Now while waiting for a Royale), and the one that leaves
  // must not silence the one that stays.
  releaseGameAudioProvider(fn: () => GameAudioState | null): boolean {
    if (this.gameAudioProvider !== fn) return false;
    this.gameAudioProvider = null;
    if (this.unlocked) this.applyMenuTrack();
    return true;
  }
  holdsGameAudioProvider(fn: () => GameAudioState | null): boolean {
    return this.gameAudioProvider === fn;
  }

  setOverrideAudioProvider(fn: (() => GameAudioState | null) | null): void {
    this.overrideProvider = fn;
    if (this.unlocked) this.applyMenuTrack();
  }

  // Keep the music off while held, whatever setPlaying wants; releasing it
  // fades the track in if a game still wants it.
  setMusicHeld(held: boolean): void {
    this.musicHeld = held;
    if (this.unlocked) { this.applyTrack(); this.applyMenuTrack(); }
  }

  // Between rounds: the round track stays on, at half volume (DUCK_LEVEL).
  setDucked(ducked: boolean): void {
    if (this.ducked === ducked) return;
    this.ducked = ducked;
    this.duckArmed = false;
    if (this.unlocked && this.musicOn) this.applyTrack();
  }

  // The next round has begun (its roll call): the duck lifts at its "3".
  armUnduck(): void {
    this.duckArmed = true;
  }

  private get musicOn(): boolean {
    return this.wantPlaying && !this.musicHeld;
  }

  // Round-over sting, fired explicitly when a game ends. Goes through the SFX bus.
  playRoundEnd(delaySec = 0): void {
    this.play("round-end", delaySec);
  }

  // A one-shot sound by name (sounds.ts), on the SFX bus at its own level,
  // `delaySec` from now.
  play(id: SoundId, delaySec = 0): void {
    this.unlock();
    if (!this.ctx || !this.sfxGain) return;
    this.playAt(id, this.ctx.currentTime + Math.max(0, delaySec));
  }
  private playAt(id: SoundId, time: number): void {
    if (!this.ctx || !this.sfxGain) return;
    const sound: { play?: (ctx: AudioContext, dest: AudioNode, t: number) => void; file?: string; level?: number } = SOUNDS[id];
    let dest: AudioNode = this.sfxGain;
    if (sound.level && sound.level !== 1) {
      const g = this.ctx.createGain();
      g.gain.value = sound.level;
      g.connect(this.sfxGain);
      dest = g;
    }
    if (sound.play) { sound.play(this.ctx, dest, time); return; }
    const buf = sound.file ? this.files.get(sound.file) : null;
    if (!buf) return; // not decoded yet (or failed): silence rather than a late sound
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(dest);
    src.start(time);
  }
  // Fetch and decode the recorded SFX once, after the first gesture.
  private loadFiles(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    for (const sound of Object.values(SOUNDS) as { file?: string }[]) {
      const url = sound.file;
      if (!url || this.files.has(url)) continue;
      this.files.set(url, null);
      fetch(url).then((r) => r.arrayBuffer()).then((b) => ctx.decodeAudioData(b)).then((buf) => { this.files.set(url, buf); }).catch(() => { /* no file: that sound stays silent */ });
    }
  }

  // Crash SFX, fired directly (not via the event-count loop). Used for a victory-lap
  // crash of your OWN worm, which is local-only (no worm_died event) so the loop
  // never sees it. Throttled like the loop so a pile-up doesn't stack.
  playCrash(): void {
    this.unlock();
    if (!this.ctx || !this.sfxGain) return;
    const t = this.ctx.currentTime;
    if (t - this.lastCrashTime <= 0.07) return;
    this.lastCrashTime = t;
    this.playAt("crash", t);
  }

  // The tick for the between-rounds "Next round in…" counter.
  playSoftTick(): void {
    this.play("next-round-tick");
  }

  // --- internals ----------------------------------------------------------------

  private applyVisibility(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (document.hidden) ctx.suspend().catch(() => {});
    else ctx.resume().catch(() => {}); // (before the first gesture this may stay suspended; that gesture starts it)
  }

  private unlock(): void {
    if (this.unlocked) return;
    this.ensureContext();
    if (!this.ctx) return;
    this.unlocked = true;
    if (this.ctx.state === "suspended" && !document.hidden) this.ctx.resume().catch(() => {});
    if (this.unlockHandler) {
      ["pointerdown", "keydown", "touchstart"].forEach((evt) =>
        window.removeEventListener(evt, this.unlockHandler as EventListener),
      );
      this.unlockHandler = null;
    }
    this.applyTrack();
    this.applyMenuTrack();
    this.loadFiles();
    this.startIntensityLoop();
  }

  private ensureContext(): void {
    if (this.ctx) return;
    const Ctor: typeof AudioContext | undefined =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return; // no Web Audio support -> silently no music
    this.ctx = new Ctor();
    // master = mute gate; music and SFX are separate buses beneath it so the
    // volume slider only scales music while mute silences everything.
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.musicGain = this.ctx.createGain();
    this.musicGain.connect(this.master);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = SFX_LEVEL;
    this.sfxGain.connect(this.master);
    this.applyVolume(getSettings());
  }

  private ensureTrack(): typeof this.track {
    if (!this.track && this.ctx && this.musicGain) {
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.musicGain);
      const src = createFileTrack(this.ctx, ROUND_TRACK, { level: 0.27, react: true });
      src.output.connect(gain);
      this.track = { src, gain, playing: false };
    }
    return this.track;
  }

  private applyTrack(): void {
    if (!this.ctx || !this.master) return;
    const track = this.musicOn ? this.ensureTrack() : this.track;
    if (!track) return;
    const now = this.ctx.currentTime;

    if (this.musicOn && !track.playing) {
      track.src.start();
      track.playing = true;
    }

    const g = track.gain.gain;
    const level = this.ducked ? DUCK_LEVEL : 1;
    // Already on (between rounds and back): glide to the new level, no new swell.
    const glide = this.musicOn && this.trackOn;
    const sec = !this.musicOn ? FADE_OUT_SEC : !glide ? FADE_IN_SEC : this.ducked ? DUCK_SEC : UNDUCK_SEC;
    const fade = () => {
      g.cancelScheduledValues(now);
      if (glide) {
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(level, now + sec);
      } else if (this.musicOn) {
        // Ease-in curve: quiet through the early countdown, blooming up into "GO".
        g.setValueAtTime(0.0001, now);
        g.setValueCurveAtTime(level === 1 ? FADE_IN_CURVE : FADE_IN_CURVE.map((v) => v * level), now, sec);
      } else {
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(0, now + sec);
      }
    };
    this.trackOn = this.musicOn;
    // Firefox refuses new events while an earlier fade-in curve is still running
    // (cancelScheduledValues(now) leaves a curve that began before now; Sentry
    // ACHTUNG-ROYALE-3): clear everything and fade from where it is.
    try { fade(); } catch {
      g.cancelScheduledValues(0);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(this.musicOn ? level : 0, now + sec);
    }

    // Stop the faded-out source shortly after the fade completes to free CPU.
    if (!this.musicOn && track.playing) {
      window.setTimeout(() => {
        if (!this.musicOn && track.playing) {
          track.src.stop();
          track.playing = false;
        }
      }, (FADE_OUT_SEC + 0.2) * 1000);
    }
  }

  // The menu track: on whenever no game holds the sound and nothing holds the music.
  private get menuOn(): boolean {
    return !this.musicOn && !this.gameAudioProvider && !this.overrideProvider && !this.musicHeld;
  }

  private applyMenuTrack(): void {
    if (!this.ctx || !this.musicGain) return;
    const on = this.menuOn;
    if (!this.menuTrack && on) {
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.musicGain);
      const src = createFileTrack(this.ctx, MENU_TRACK, { level: 0.22 });
      src.output.connect(gain);
      this.menuTrack = { src, gain, playing: false };
    }
    const track = this.menuTrack;
    if (!track) return;
    const now = this.ctx.currentTime;
    if (on && !track.playing) {
      track.src.start();
      track.playing = true;
    }
    track.gain.gain.cancelScheduledValues(now);
    track.gain.gain.setValueAtTime(track.gain.gain.value, now);
    track.gain.gain.linearRampToValueAtTime(on ? 1 : 0, now + (on ? MENU_FADE_IN_SEC : MENU_FADE_OUT_SEC));
    if (!on && track.playing) {
      window.setTimeout(() => {
        if (!this.menuOn && track.playing) {
          track.src.stop();
          track.playing = false;
        }
      }, (MENU_FADE_OUT_SEC + 0.2) * 1000);
    }
  }

  private applyVolume(s: Settings): void {
    if (!this.ctx || !this.master || !this.musicGain) return;
    const now = this.ctx.currentTime;
    // Mute gates everything; the volume slider scales only the music bus.
    const ramp = (param: AudioParam, target: number) => {
      param.cancelScheduledValues(now);
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(target, now + 0.12);
    };
    ramp(this.master.gain, s.muted || this.externalMute ? 0 : 1);
    ramp(this.musicGain.gain, (s.volume / 100) * MUSIC_HEADROOM);
  }

  private startIntensityLoop(): void {
    if (this.rafId || typeof window === "undefined") return;
    const loop = () => {
      const provider = this.overrideProvider || this.gameAudioProvider;
      const state = provider ? provider() : null;

      // Feed (smoothed) intensity into the track so it glides between energy
      // levels rather than jumping on player eliminations.
      const target = state ? Math.max(0, Math.min(1, state.intensity)) : 0.3;
      this.intensity += (target - this.intensity) * 0.05;
      if (this.track) this.track.src.setIntensity(this.intensity);

      // Paused/scrubbing replay: keep the music (intensity above) but play no
      // SFX. Sync the counters to the scrubbed position so resuming only fires on
      // genuinely new events from here, not everything we seeked past.
      if (state && state.suppressSfx) {
        const left = state.countdownLeft;
        this.lastBeepCount = left > 0 ? Math.ceil(left) : 0;
        this.goFired = left <= 0;
        this.lastCrashes = state.crashes;
        this.lastPickups = state.pickups;
        this.lastGreen = state.pickupsGreen || 0;
        this.lastRed = state.pickupsRed || 0;
        this.lastSpawns = state.spawns || 0;
        this.lastMissileHits = state.missileHits || 0;
        this.lastOwnCrashes = state.ownCrashes || 0;
        this.lastSwitches = state.switches;
        this.lastPortalOpenAt = state.portalOpenAt || 0;
        this.rafId = window.requestAnimationFrame(loop);
        return;
      }

      // Countdown beeps: a blip as each of 3 / 2 / 1 appears, then the "GO" blip
      // the instant the round starts. Routed to the SFX bus (fixed level, not the
      // music-volume slider) so they cut through the swelling track.
      if (state && this.ctx && this.sfxGain) {
        const left = state.countdownLeft;
        // Ducked between rounds: back up as the next round's countdown shows
        // its "3", not through the roll call. (Only once the game says the new
        // round is on: until then the finished round's clock is read.)
        if (this.ducked && this.duckArmed) {
          if ((left <= 0 || state.countdownLength === undefined || Math.ceil(left) <= Math.ceil(state.countdownLength))) this.setDucked(false);
        }
        if (state.silentCountdown) {
          // keep the bookkeeping in step, sound nothing
          this.lastBeepCount = left > 0 ? Math.ceil(left) : 0;
          this.goFired = left <= 0;
        } else if (left > 0) {
          this.goFired = false;
          const count = Math.ceil(left); // counts down from the countdown length (3s)
          // Pip on every count of the countdown proper, but not through the
          // roll call before it.
          // (countdownLength is millisecond-rounded by core, so 3 means 3, not 3.0000000004)
          const within = state.countdownLength === undefined || count <= Math.ceil(state.countdownLength);
          if (count >= 1 && within && count !== this.lastBeepCount) {
            this.lastBeepCount = count;
            playCountdownCount(this.ctx, this.sfxGain, this.ctx.currentTime);
          }
        } else if (!this.goFired) {
          this.goFired = true;
          this.lastBeepCount = 0;
          playCountdownGo(this.ctx, this.sfxGain, this.ctx.currentTime);
        }

        // Crash SFX on each new elimination (throttled for mass pile-ups). A new
        // round resets the cumulative count, so guard against going backwards.
        // Our own crash gets the heavier sound instead of the plain one.
        const own = state.ownCrashes || 0;
        if (own > this.lastOwnCrashes) {
          const t = this.ctx.currentTime;
          this.playAt("own-crash", t);
          this.lastCrashTime = t;
        } else if (state.crashes > this.lastCrashes) {
          const t = this.ctx.currentTime;
          if (t - this.lastCrashTime > 0.07) {
            this.playAt("crash", t);
            this.lastCrashTime = t;
          }
        }
        this.lastOwnCrashes = own;
        this.lastCrashes = state.crashes;

        // Pickup SFX on each new power-up taken, by its colour. A red one fires
        // missiles at every rival: their launch rides on its pickup sound.
        const green = state.pickupsGreen || 0, red = state.pickupsRed || 0;
        if (state.pickups > this.lastPickups) {
          const t = this.ctx.currentTime;
          if (green > this.lastGreen) this.playAt("pickup-green", t);
          else if (red > this.lastRed) { this.playAt("pickup-red", t); this.playAt("missile", t + 0.06); }
          else this.playAt("pickup-blue", t);
        }
        this.lastPickups = state.pickups;
        this.lastGreen = green;
        this.lastRed = red;

        // A power-up appearing, and a missile landing.
        const spawns = state.spawns || 0;
        if (spawns > this.lastSpawns) this.playAt("powerup-spawn", this.ctx.currentTime);
        this.lastSpawns = spawns;
        const hits = state.missileHits || 0;
        if (hits > this.lastMissileHits) this.playAt("missile-hit", this.ctx.currentTime);
        this.lastMissileHits = hits;

        // Swap SFX on each new switcharoonie (distinct from the pickup arpeggio).
        if (state.switches > this.lastSwitches) {
          playSwitcharoonie(this.ctx, this.sfxGain, this.ctx.currentTime);
        }
        this.lastSwitches = state.switches;

        // Switcharoonie: the portal riser as the wormholes open (everyone confirmed
        // frozen) - the sequence's only sound. Marker-based: fire once per new
        // nonzero timestamp (back-to-back switches carry distinct timestamps).
        const portalAt = state.portalOpenAt || 0;
        if (portalAt && portalAt !== this.lastPortalOpenAt) {
          playPortalOpen(this.ctx, this.sfxGain, this.ctx.currentTime);
        }
        this.lastPortalOpenAt = portalAt;
      } else if (!state) {
        // No game live — reset so the next round counts down / counts fresh.
        this.lastBeepCount = -1;
        this.goFired = false;
        this.lastCrashes = 0;
        this.lastPickups = 0;
        this.lastGreen = 0;
        this.lastRed = 0;
        this.lastSpawns = 0;
        this.lastMissileHits = 0;
        this.lastOwnCrashes = 0;
        this.lastSwitches = 0;
        this.lastPortalOpenAt = 0;
      }

      this.rafId = window.requestAnimationFrame(loop);
    };
    this.rafId = window.requestAnimationFrame(loop);
  }
}

const controller = new MusicController();
export default controller;
